import type { Camera } from "../native/geometry";
import type { Point } from "../scene/model";
import { clampZoom } from "../native/viewport";

/** Capture before tools: a second finger cancels drawing, then controls only the viewport. */
export function installNativePinch(
  root: HTMLElement,
  overlay: HTMLCanvasElement,
  camera: Camera,
  blocked: () => boolean,
  changed: () => void,
) {
  const points = new Map<number, Point>();
  let pinching = false;
  let start: { distance: number; zoom: number; anchor: Point } | null = null;
  const pair = () => {
    const [a, b] = [...points.values()];
    return a && b
      ? {
          distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
          center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        }
      : null;
  };
  const reset = () => {
    points.clear();
    pinching = false;
    start = null;
  };
  const move = () => {
    const current = pair();
    if (!start || !current) return;
    camera.zoom = clampZoom((start.zoom * current.distance) / start.distance);
    camera.x = current.center.x - start.anchor.x * camera.zoom;
    camera.y = current.center.y - start.anchor.y * camera.zoom;
    changed();
  };
  for (const type of [
    "pointerdown",
    "pointermove",
    "pointerup",
    "pointercancel",
  ] as const) {
    root.addEventListener(
      type,
      (event) => {
        if (event.pointerType !== "touch") return;
        if (blocked()) {
          reset();
          return;
        }
        if (type === "pointerdown") {
          if (
            event.target !== overlay &&
            (event.target as HTMLElement).id !== "native-text-input"
          )
            return;
          points.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (points.size === 2 && !pinching) {
            const current = pair()!;
            pinching = true;
            // Existing tools own rollback and capture cleanup for their first-finger gesture.
            overlay.dispatchEvent(
              new PointerEvent("pointercancel", {
                bubbles: true,
                pointerId: [...points.keys()][0]!,
              }),
            );
            start = {
              distance: current.distance,
              zoom: camera.zoom,
              anchor: {
                x: (current.center.x - camera.x) / camera.zoom,
                y: (current.center.y - camera.y) / camera.zoom,
              },
            };
            for (const id of points.keys()) overlay.setPointerCapture(id);
          }
        } else if (points.has(event.pointerId)) {
          points.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (pinching && type !== "pointercancel") move();
        } else return;
        const consume = pinching;
        if (type === "pointerup" || type === "pointercancel") {
          points.delete(event.pointerId);
          if (consume && overlay.hasPointerCapture(event.pointerId))
            overlay.releasePointerCapture(event.pointerId);
          if (!points.size) reset();
        }
        if (consume) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  }
  window.addEventListener("blur", reset);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) reset();
  });
}
